/**
 * Curated live purchase + review activity toasts (Aaramya)
 */
(function () {
  const STORAGE_KEY = 'aa_live_activity_dismissed';

  function shouldSkip() {
    const main = document.querySelector('#MainContent');
    const template = main && main.getAttribute('data-template');
    if (template === 'cart') return true;
    if (template === 'product') return true;
    if (/^\/products\//i.test(location.pathname)) return true;
    if (/^\/cart\/?$/i.test(location.pathname)) return true;
    if (/\/checkouts?\//i.test(location.pathname)) return true;
    if (sessionStorage.getItem(STORAGE_KEY) === '1') return true;
    return false;
  }

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function eventKey(event) {
    return `${event.type}|${event.name}|${event.productTitle || ''}`;
  }

  function shuffleEvents(list) {
    const arr = list.slice();
    for (let i = arr.length - 1; i > 0; i -= 1) {
      const j = Math.floor(Math.random() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
  }

  function pickNextEvent(events, lastKey, recentKeys) {
    let pool = events.filter((e) => eventKey(e) !== lastKey);
    if (!pool.length) pool = events.slice();

    const fresh = pool.filter((e) => !recentKeys.has(eventKey(e)));
    const pickFrom = fresh.length ? fresh : pool;
    return pickFrom[Math.floor(Math.random() * pickFrom.length)];
  }

  function initial(name) {
    const n = (name || '').trim();
    return n ? n.charAt(0).toUpperCase() : '?';
  }

  function starsHtml(count) {
    const n = Math.max(3, Math.min(5, Number(count) || 5));
    return '★'.repeat(n);
  }

  function buildMessage(event) {
    if (event.type === 'purchase') {
      const place = event.city ? ` from ${event.city}` : '';
      const product = event.productTitle || 'a product';
      return `<strong>${event.name}</strong>${place} recently purchased <strong>${product}</strong>`;
    }
    const product = event.productTitle || 'a product';
    const stars = starsHtml(event.stars);
    let html = `<strong>${event.name}</strong> shared a <span class="aa-live-toast__stars">${stars}</span> review for <strong>${product}</strong>`;
    if (event.quote) {
      html += `<span class="aa-live-toast__quote">"${event.quote}"</span>`;
    }
    return html;
  }

  function createToast(event) {
    const toast = document.createElement('div');
    toast.className = 'aa-live-toast';
    toast.setAttribute('role', 'status');
    toast.setAttribute('aria-live', 'polite');

    const avatar = document.createElement('span');
    avatar.className = 'aa-live-toast__avatar';
    avatar.textContent = initial(event.name);
    avatar.setAttribute('aria-hidden', 'true');

    const body = document.createElement('div');
    body.className = 'aa-live-toast__body';
    body.innerHTML = buildMessage(event);

    if (event.timeAgo && event.type === 'purchase') {
      const meta = document.createElement('span');
      meta.className = 'aa-live-toast__meta';
      meta.textContent = `Recent customer activity · ${event.timeAgo}`;
      body.appendChild(meta);
    }

    const mediaWrap = document.createElement('div');
    mediaWrap.className = 'aa-live-toast__media';

    if (event.productImage) {
      const img = document.createElement('img');
      img.src = event.productImage;
      img.alt = '';
      img.width = 48;
      img.height = 48;
      img.loading = 'lazy';
      mediaWrap.appendChild(img);
    }

    if (event.productUrl) {
      toast.classList.add('aa-live-toast--linked');
      toast.addEventListener('click', (e) => {
        if (e.target.closest('.aa-live-toast__close')) return;
        window.location.href = event.productUrl;
      });
    }

    const close = document.createElement('button');
    close.type = 'button';
    close.className = 'aa-live-toast__close';
    close.setAttribute('aria-label', 'Dismiss notifications');
    close.innerHTML = '&times;';
    close.addEventListener('click', (e) => {
      e.stopPropagation();
      sessionStorage.setItem(STORAGE_KEY, '1');
      hideToast(toast);
      setTimeout(() => toast.remove(), 300);
    });

    toast.appendChild(avatar);
    toast.appendChild(body);
    if (event.productImage) toast.appendChild(mediaWrap);
    toast.appendChild(close);

    return toast;
  }

  function hideToast(toast) {
    toast.classList.remove('is-visible');
  }

  function showToast(toast, durationMs, reducedMotion) {
    const host = document.querySelector('[data-aa-live-toast-host]');
    if (!host) return;

    host.appendChild(toast);
    requestAnimationFrame(() => {
      toast.classList.add('is-visible');
    });

    return new Promise((resolve) => {
      const t = setTimeout(() => {
        hideToast(toast);
        setTimeout(() => {
          toast.remove();
          resolve();
        }, reducedMotion ? 0 : 320);
      }, durationMs);
      toast._hideTimer = t;
    });
  }

  async function runLoop(config, events) {
    if (!events.length) return;

    const reducedMotion = prefersReducedMotion();
    const firstDelay = Number(config.firstDelay) * 1000 || 10000;
    const toastDuration = Number(config.toastDuration) * 1000 || 6000;
    // Cadence requested: first popup at 10s, then 1m, 2m, 5m, 10m between subsequent popups.
    // After reaching 10m, keep using 10m for later popups.
    const followUpGapSeconds = [60, 120, 300, 600];
    function gapBeforeNext(shownCount) {
      const idx = Math.max(0, shownCount - 1);
      return (followUpGapSeconds[idx] || 600) * 1000;
    }

    if (config.showMobile === 'false' && window.innerWidth < 750) return;

    let host = document.querySelector('[data-aa-live-toast-host]');
    if (!host) {
      host = document.createElement('div');
      host.className = 'aa-live-toast-host';
      host.setAttribute('data-aa-live-toast-host', '');
      document.body.appendChild(host);
    }

    await new Promise((r) => setTimeout(r, firstDelay));

    let queue = shuffleEvents(events);
    let lastKey = '';
    const recentKeys = new Set();
    const recentLimit = Math.min(8, Math.max(4, Math.floor(events.length * 0.45)));
    let shownCount = 0;

    while (!sessionStorage.getItem(STORAGE_KEY)) {
      if (shouldSkip()) break;

      if (!queue.length) queue = shuffleEvents(events);

      const event = pickNextEvent(queue, lastKey, recentKeys);
      queue = queue.filter((e) => eventKey(e) !== eventKey(event));

      const key = eventKey(event);
      lastKey = key;
      recentKeys.add(key);
      if (recentKeys.size > recentLimit) {
        const first = recentKeys.values().next().value;
        recentKeys.delete(first);
      }

      const toast = createToast(event);
      await showToast(toast, toastDuration, reducedMotion);
      shownCount += 1;

      if (sessionStorage.getItem(STORAGE_KEY)) break;

      await new Promise((r) => setTimeout(r, gapBeforeNext(shownCount)));
    }
  }

  function init() {
    if (shouldSkip()) return;

    const root = document.querySelector('[data-aa-live-activity]');
    if (!root || root.getAttribute('data-enabled') !== 'true') return;

    const dataEl = document.getElementById('aa-live-activity-data');
    if (!dataEl) return;

    let events;
    try {
      events = JSON.parse(dataEl.textContent || '[]');
    } catch (e) {
      return;
    }
    if (!Array.isArray(events) || !events.length) return;

    const config = {
      firstDelay: root.getAttribute('data-first-delay'),
      toastDuration: root.getAttribute('data-toast-duration'),
      showMobile: root.getAttribute('data-show-mobile'),
    };

    runLoop(config, events);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
